import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { Gauge } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import {
  documentoDelResumen,
  subtituloDelResumen,
  textoDeTasa,
} from "@/componentes/resumen/exportarResumen"
import type { Resumen } from "@/componentes/resumen/tipos"
import { VistaResumen } from "@/componentes/resumen/VistaResumen"
import { monto } from "@/componentes/tesoreria/dinero"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { comoFecha } from "@/lib/fechas"

/**
 * Resumen Macro: el alcance de la disponibilidad presupuestaria. Ver
 * `componentes/resumen/VistaResumen.tsx` (el mismo cuerpo pinta la foto del enlace
 * público, `/m/<token>`) y `services/resumen_macro.py`.
 *
 * ⚠ **Sólo «Exportar»** (29/10, pedido del usuario): el PDF y el Excel salen con el
 * membrete, las cifras de los cuatro pasos como indicadores y todo el detalle. «Imprimir /
 * PDF» —la impresión del navegador— se retiró.
 *
 * ⚠ **El selector cambia sólo a qué tasa se valoran los bolívares, y sólo en esta pantalla** (30/10,
 * decisión del usuario): USD y USDT van 1:1 siempre. El Planificador y el reporte de los jueves
 * siguen con BCV, por eso se pide también el resumen base.
 */
export function ResumenMacro() {
  const [tasa, setTasa] = useState("USD")
  const base = useQuery({
    queryKey: ["resumen-macro"],
    queryFn: () => api.get<Resumen>("/api/v1/resumen-macro"),
  })
  const elegida = useQuery({
    queryKey: ["resumen-macro", "tasa", tasa],
    queryFn: () => api.get<Resumen>(`/api/v1/resumen-macro?tasa_bs=${tasa}`),
    enabled: tasa !== "USD",
    placeholderData: keepPreviousData,
  })
  const resumen = tasa === "USD" ? base : elegida

  if (resumen.isPending) return <EstadoCarga />
  if (resumen.isError) {
    return (
      <EstadoError
        titulo="No se pudo calcular el resumen"
        error={resumen.error}
        onReintentar={() => resumen.refetch()}
      />
    )
  }
  const r = resumen.data
  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Gauge size={20} aria-hidden={true} /> Alcance de la disponibilidad
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Resumen macro al {comoFecha(r.fecha)}, {textoDeTasa(r)}. Se calcula al abrirlo.
          </p>
        </div>
        <div className="no-print flex flex-wrap items-end gap-2">
          {(base.data?.tasas_bs?.length ?? 0) > 0 && (
            <label className="block text-xs" style={{ color: "var(--fg-muted)" }}>
              Tasa de los bolívares
              <select
                className="campo mt-1 tabular-nums"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
              >
                {base.data?.tasas_bs?.map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.nombre} · {monto(t.valor)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Exportar
            titulo="Resumen Macro"
            subtitulo={subtituloDelResumen(r)}
            documento={() => documentoDelResumen(r)}
          />
        </div>
      </header>
      <VistaResumen r={r} planificador={base.data} />
    </div>
  )
}
